import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { actionApiToActionEntity, ActionStatus, actionToActionApiDetails, ActionType, convertActionStatusToActionApiStatus, convertActionTypeToActionApiType } from '../../entities/Action';
import { pagingMetadataToCursorResponse } from '../../utils/PagingUtils';
import { BaseClient } from '../BaseClient';
export class ActionClientBase extends BaseClient {
  constructor() {
    super(...arguments);
    _defineProperty(this, "getActionImpl", void 0);
    _defineProperty(this, "queryActionsImpl", void 0);
    _defineProperty(this, "createActionWithContactsImpl", void 0);
    _defineProperty(this, "createAnonymousActionImpl", void 0);
    _defineProperty(this, "bulkCreateActionsWithContactsImpl", void 0);
    _defineProperty(this, "updateActionImpl", void 0);
    _defineProperty(this, "deleteActionImpl", void 0);
  }
  convertToApiCreatableEntity(data) {
    return {
      action: {
        taskId: data.taskId,
        guidelines: data.guidelines,
        status: convertActionStatusToActionApiStatus(data.status),
        actionType: convertActionTypeToActionApiType(data.actionType),
        recurringScheduleId: data.recurringScheduleId,
        ...actionToActionApiDetails({
          actionType: data.actionType,
          details: data.details
        })
      },
      contactIds: data.contactIds
    };
  }
  async getAction(actionId) {
    try {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }
      const {
        action
      } = await this.request(this.getActionImpl({
        actionId
      }), ...args);
      return this.convertToEntity(action);
    } catch (error) {
      this.logError('getAction', 'Failed to get action', {
        actionId
      }, error);
      throw error;
    }
  }
  async queryActions(query) {
    try {
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }
      const {
        actions,
        pagingMetadata
      } = await this.request(this.queryActionsImpl({
        query
      }), ...args);
      if (!actions) {
        throw new Error('Actions is not an array');
      }
      return {
        items: actions == null ? void 0 : actions.map(this.convertToEntity),
        ...pagingMetadataToCursorResponse(pagingMetadata)
      };
    } catch (error) {
      this.logError('queryActions', 'Failed to query actions', {
        query
      }, error);
      throw error;
    }
  }
  async queryActionsByTaskId(taskId, query) {
    try {
      for (var _len3 = arguments.length, args = new Array(_len3 > 2 ? _len3 - 2 : 0), _key3 = 2; _key3 < _len3; _key3++) {
        args[_key3 - 2] = arguments[_key3];
      }
      const {
        actions,
        pagingMetadata
      } = await this.request(this.queryActionsImpl({
        query: {
          ...query,
          filter: {
            ...(query == null ? void 0 : query.filter),
            taskId
          }
        }
      }), ...args);
      if (!actions) {
        throw new Error('Actions is not an array');
      }
      return {
        items: actions == null ? void 0 : actions.map(this.convertToEntity),
        ...pagingMetadataToCursorResponse(pagingMetadata)
      };
    } catch (error) {
      this.logError('queryActionsByTaskId', 'Failed to query actions by task ID', {
        taskId,
        query
      }, error);
      throw error;
    }
  }
  async createAction(data) {
    try {
      const creatableAction = this.convertToApiCreatableEntity(data);
      for (var _len4 = arguments.length, args = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
        args[_key4 - 1] = arguments[_key4];
      }
      const {
        action
      } = await this.request(this.createActionWithContactsImpl(creatableAction), ...args);
      return this.convertToEntity(action);
    } catch (error) {
      this.logError('createAction', 'Failed to create action', {
        data
      }, error);
      throw error;
    }
  }
  async createAnonymousAction(data) {
    try {
      for (var _len5 = arguments.length, args = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
        args[_key5 - 1] = arguments[_key5];
      }
      const {
        action,
        participant
      } = await this.request(this.createAnonymousActionImpl({
        serviceId: data.serviceId,
        providerId: data.providerId,
        contactId: data.contactId,
        conversationId: data.conversationId
      }), ...args);
      return {
        action: this.convertToEntity(action),
        participant: participant
      };
    } catch (error) {
      this.logError('createAnonymousAction', 'Failed to create anonymous action', {
        data
      }, error);
      throw error;
    }
  }
  async bulkCreateActions(data) {
    try {
      const creatableActions = data.map(this.convertToApiCreatableEntity);
      for (var _len6 = arguments.length, args = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
        args[_key6 - 1] = arguments[_key6];
      }
      const {
        results
      } = await this.request(this.bulkCreateActionsWithContactsImpl({
        actionsWithContacts: creatableActions,
        returnEntity: true
      }), ...args);
      if (!results) {
        return [];
      }
      return results.map(r => this.convertToEntity(r.item));
    } catch (error) {
      this.logError('bulkCreateAction', 'Failed to bulk create actions', {
        data
      }, error);
      throw error;
    }
  }
  async updateActionStatus(action, status) {
    try {
      for (var _len7 = arguments.length, args = new Array(_len7 > 2 ? _len7 - 2 : 0), _key7 = 2; _key7 < _len7; _key7++) {
        args[_key7 - 2] = arguments[_key7];
      }
      const response = await this.request(this.updateActionImpl({
        action: {
          ...action,
          status: convertActionStatusToActionApiStatus(status)
        },
        fieldMask: ['status']
      }), ...args);
      return this.convertToEntity(response.action);
    } catch (error) {
      this.logError('updateAction', 'Failed to update action status', {
        action,
        status
      }, error);
      throw error;
    }
  }
  async updateActionProgressSummary(actionId, progressSummary) {
    try {
      for (var _len8 = arguments.length, args = new Array(_len8 > 2 ? _len8 - 2 : 0), _key8 = 2; _key8 < _len8; _key8++) {
        args[_key8 - 2] = arguments[_key8];
      }
      const {
        action: fullAction
      } = await this.request(this.getActionImpl({
        actionId
      }), ...args);
      if (!fullAction) {
        throw new Error(`Can't find action`);
      }
      const actionToUpdate = {
        ...fullAction,
        progressSummary
      };
      const response = await this.request(this.updateActionImpl({
        action: actionToUpdate,
        fieldMask: ['progressSummary']
      }), ...args);
      return this.convertToEntity(response.action);
    } catch (error) {
      this.logError('updateActionProgressSummary', 'Failed to update action progress summary', {
        actionId,
        progressSummary
      }, error);
      throw error;
    }
  }
  async updateScheduleMeetingActionCompleted(actionId, scheduledAt, meetingId, participantEventId, numberOfRetries) {
    this.logInfo('updateScheduleMeetingActionCompleted', 'start', {
      actionId,
      scheduledAt,
      meetingId,
      participantEventId
    });
    for (var _len9 = arguments.length, args = new Array(_len9 > 5 ? _len9 - 5 : 0), _key9 = 5; _key9 < _len9; _key9++) {
      args[_key9 - 5] = arguments[_key9];
    }
    try {
      const {
        action
      } = await this.request(this.getActionImpl({
        actionId
      }), ...args);
      if (!action) {
        throw new Error(`Can't find action`);
      }
      if (action.actionType !== ActionType.SCHEDULE_MEETING) {
        throw new Error(`Invalid action type: ${action.actionType}`);
      }
      const actionToUpdate = {
        actionType: action.actionType,
        id: action.id,
        revision: action.revision,
        scheduleMeetingDetails: {
          ...action.scheduleMeetingDetails,
          scheduledAt,
          meetingId,
          participantEventId
        },
        status: convertActionStatusToActionApiStatus(ActionStatus.COMPLETE)
      };
      this.logInfo('updateScheduleMeetingActionCompleted', 'before call', {
        actionToUpdate
      });
      const response = await this.request(this.updateActionImpl({
        action: actionToUpdate,
        fieldMask: ['status', 'scheduleMeetingDetails']
      }), ...args);
      this.logInfo('updateScheduleMeetingActionCompleted', 'response', {
        response
      });
      return this.convertToEntity(response.action);
    } catch (error) {
      if (numberOfRetries > 0) {
        this.logInfo('updateScheduleMeetingActionCompleted', 'failed - retrying', {
          error,
          numberOfRetries
        });
        return this.updateScheduleMeetingActionCompleted(actionId, scheduledAt, meetingId, participantEventId, numberOfRetries - 1, ...args);
      }
      this.logError('updateScheduleMeetingActionCompleted', 'Failed to update schedule meeting action completed', {
        actionId,
        scheduledAt,
        meetingId
      }, error);
      throw error;
    }
  }
  async updateAction(data) {
    try {
      for (var _len0 = arguments.length, args = new Array(_len0 > 1 ? _len0 - 1 : 0), _key0 = 1; _key0 < _len0; _key0++) {
        args[_key0 - 1] = arguments[_key0];
      }
      const {
        action
      } = await this.request(this.updateActionImpl({
        action: {
          ...data,
          status: convertActionStatusToActionApiStatus(data.status),
          actionType: convertActionTypeToActionApiType(data.actionType),
          progressSummary: data.progressSummary,
          ...actionToActionApiDetails({
            actionType: data.actionType,
            details: data.details
          })
        },
        fieldMask: ['guidelines', 'status', 'recurringScheduleId', 'actionType', 'details', 'progressSummary'] // no taskId - on purpose
      }), ...args);
      return this.convertToEntity(action);
    } catch (error) {
      this.logError('updateAction', 'Failed to update action', {
        data
      }, error);
      throw error;
    }
  }
  async deleteAction(actionId) {
    try {
      for (var _len1 = arguments.length, args = new Array(_len1 > 1 ? _len1 - 1 : 0), _key1 = 1; _key1 < _len1; _key1++) {
        args[_key1 - 1] = arguments[_key1];
      }
      await this.request(this.deleteActionImpl({
        actionId
      }), ...args);
    } catch (error) {
      this.logError('deleteAction', 'Failed to delete action', {
        actionId
      }, error);
      throw error;
    }
  }
  convertToEntity(action) {
    try {
      return actionApiToActionEntity(action);
    } catch (error) {
      this.logError('convertToEntity', 'Failed to convert action', {
        action
      }, error);
      throw error;
    }
  }
  getServiceNameForLogs() {
    return 'ActionsClient';
  }
}
//# sourceMappingURL=ActionClientBase.js.map